import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ChevronDown, CalendarDays, Plus, RefreshCw, X, Video, Link as LinkIcon, ExternalLink } from 'lucide-react';
import GridMes, { TagDia } from './GridMes';
import useAgendaMs from './useAgendaMs';
import useCalendarioMes from './useCalendarioMes';
import FormCompromisso from './FormCompromisso';
import FormEvento from './FormEvento';
import PainelFeriados from './PainelFeriados';
import { parseLocal, horaBR, novoCompromisso, compromissoParaForm } from './agenda-utils';
import { CAMADA, MESES, PERFIS_GESTAO_FERIADO } from './calendario-ui';
import { COR, FONTE } from './ui';

// =====================================================================
// CALENDÁRIO DA AGENDA — o mês inteiro, com as 4 camadas
// =====================================================================
// É o conteúdo do modal que abre pelo ícone de calendário do cabeçalho. Mesmo
// desenho da tela /aniversariantes (grid de mês, tags coloridas por dia), com uma
// camada a mais: os compromissos do Teams/M365 do próprio usuário.
//
// AS 4 CAMADAS E QUEM PODE CRIAR CADA UMA (o menu "Novo" reflete isto):
//   - Compromisso (Teams, roxo) : escritório/gerente que esteja conectado ao M365
//     (as DUAS coisas). É a agenda PESSOAL — só o dono vê e edita.
//   - Evento (verde)   : escritório/gerente (2026-07-16). Privado ou compartilhado
//     por perfil, loja e/ou pessoa; pode espelhar no Teams.
//   - Feriado (amarelo): SÓ Admin/Marketing (authorizeRoles no backend) — NÃO segue
//     a regra escritório/gerente: feriado vale para a rede toda.
//   - Para os demais a opção nem aparece, senão levariam 403 ao salvar. Quem não
//     pode criar nada não vê o botão "Novo" — só consulta.
//   - Aniversário (vermelho): NINGUÉM cria. É derivado do DATA_NASCIMENTO da
//     dtbConfere (recarregada todo dia do RH) e o endpoint só tem GET — por isso
//     está na legenda, mas não no menu "Novo".
//
// Fusos: os compromissos do Graph já chegam em horário de Brasília; as camadas
// internas são datas puras (sem hora). Ver agenda-utils.parseLocal.
//
// Props:
//   - onFechar : fecha o modal (o × fica no cabeçalho daqui)

function CalendarioAgenda({ onFechar }) {
    const navigate = useNavigate();
    const hoje = new Date();
    // ano E mês navegáveis: passar de dezembro leva a janeiro do ano SEGUINTE.
    const [ano, setAno] = useState(hoje.getFullYear());
    const [mes, setMes] = useState(hoje.getMonth() + 1);   // 1..12
    const [form, setForm] = useState(null);                // null | form do compromisso Teams
    const [eventoAberto, setEventoAberto] = useState(false);
    const [feriadosAberto, setFeriadosAberto] = useState(false);
    const [menuAberto, setMenuAberto] = useState(false);   // menu de tipo do "Novo"
    const menuRef = useRef(null);

    const agenda = useAgendaMs();
    const interno = useCalendarioMes(ano, mes);
    const { estado, carregarEventos } = agenda;

    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const podeFeriado = PERFIS_GESTAO_FERIADO.includes(user?.perfil);

    // Busca os compromissos do Teams sempre que a conexão ficar pronta OU o mês
    // mudar. A janela é o mês inteiro (do dia 1 às 00:00 ao dia 1 do mês seguinte).
    useEffect(() => {
        if (estado !== 'conectado') return;
        const ini = new Date(ano, mes - 1, 1, 0, 0, 0);
        const fim = new Date(ano, mes, 1, 0, 0, 0);
        carregarEventos(ini, fim);
    }, [estado, ano, mes, carregarEventos]);

    // Fecha o menu de tipo ao clicar fora.
    useEffect(() => {
        if (!menuAberto) return;
        const aoClicarFora = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) setMenuAberto(false);
        };
        document.addEventListener('mousedown', aoClicarFora);
        return () => document.removeEventListener('mousedown', aoClicarFora);
    }, [menuAberto]);

    // Compromissos do Teams agrupados por dia do mês exibido.
    const teamsPorDia = {};
    agenda.eventos.forEach((ev) => {
        const dt = parseLocal(ev.inicio);
        if (!dt || dt.getFullYear() !== ano || dt.getMonth() + 1 !== mes) return;
        (teamsPorDia[dt.getDate()] = teamsPorDia[dt.getDate()] || []).push(ev);
    });

    const irMes = (passo) => {
        const d = new Date(ano, mes - 1 + passo, 1);
        setAno(d.getFullYear());
        setMes(d.getMonth() + 1);
    };

    const salvarForm = async () => {
        const ok = await agenda.salvar(form);
        if (ok) setForm(null);
    };
    const excluirForm = async () => {
        const ok = await agenda.excluir({ id: form.id, assunto: form.assunto || 'este compromisso', serie: form.serie });
        if (ok) setForm(null);
    };

    // O que o menu "Novo" oferece, conforme conexão e permissão. Aniversário não
    // entra aqui de propósito (ver cabeçalho).
    // Compromisso exige as DUAS coisas: permissão (escritório/gerente) E conexão
    // com o M365 — sem conta vinculada não há em nome de quem criar.
    // As duas respostas vêm do backend (agenda.podeCriar do /api/ms/status e
    // opcoes.podeCriar do /api/calendario/opcoes): a regra mora num lugar só.
    const tiposNovos = [
        { chave: 'teams', ...CAMADA.teams, disponivel: estado === 'conectado' && agenda.podeCriar, abrir: () => setForm(novoCompromisso()) },
        { chave: 'evento', ...CAMADA.evento, disponivel: !!interno.opcoes.podeCriar, abrir: () => setEventoAberto(true) },
        { chave: 'feriado', ...CAMADA.feriado, disponivel: podeFeriado, abrir: () => setFeriadosAberto(true) },
    ].filter((t) => t.disponivel);

    // Feriado não pinta mais a célula: a tag amarela já basta e o fundo creme
    // brigaria com a borda amarela do "hoje" (viraria a mesma pista visual).
    const renderDia = (dia) => (
        <>
            {(interno.feriadosPorDia[dia] || []).map((f, j) => (
                <TagDia
                    key={`f${j}`} {...CAMADA.feriado} texto={f.nome}
                    title={`Feriado ${f.tipo}${f.municipio ? ' · ' + f.municipio : ''}${f.uf ? '/' + f.uf : ''}: ${f.nome}`}
                />
            ))}
            {(interno.eventosPorDia[dia] || []).map((ev) => (
                <TagDia key={`e${ev.id}`} {...CAMADA.evento} texto={ev.titulo} title={ev.titulo} />
            ))}
            {(teamsPorDia[dia] || []).map((ev) => {
                const ini = parseLocal(ev.inicio), fim = parseLocal(ev.fim);
                const hora = ev.diaInteiro ? 'Dia todo' : `${horaBR(ini)}–${horaBR(fim)}`;
                return (
                    <TagDia
                        key={`t${ev.id}`} {...CAMADA.teams}
                        icone={ev.online ? <Video size={10} style={{ flexShrink: 0 }} /> : null}
                        texto={`${ev.diaInteiro ? '' : horaBR(ini) + ' '}${ev.assunto}`}
                        title={`${hora} · ${ev.assunto}${ev.local ? ' · ' + ev.local : ''} (clique para editar)`}
                        onClick={() => setForm(compromissoParaForm(ev))}
                    />
                );
            })}
            {(interno.anivPorDia[dia] || []).map((p, j) => (
                <TagDia
                    key={`a${j}`} {...CAMADA.aniversario}
                    texto={(p.nome || '').split(' ')[0]}
                    title={`${p.nome} · ${p.cargo} · ${p.loja}`}
                />
            ))}
        </>
    );

    const btnNav = { background: 'none', border: 'none', cursor: 'pointer', color: COR.vermelhoEscuro, display: 'flex', alignItems: 'center', padding: '2px' };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0, fontFamily: FONTE }}>
            {/* ---- Cabeçalho: título · navegação de mês · novo · fechar ---- */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap', marginBottom: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <CalendarDays size={24} color={COR.vermelhoEscuro} />
                    <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: COR.texto }}>Agenda Corporativa</h2>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <button onClick={() => irMes(-1)} title="Mês anterior" style={btnNav}><ChevronLeft size={20} /></button>
                        <span style={{ minWidth: '150px', textAlign: 'center', fontWeight: 700, fontSize: '15px', color: COR.texto }}>{MESES[mes - 1]} {ano}</span>
                        <button onClick={() => irMes(1)} title="Próximo mês" style={btnNav}><ChevronRight size={20} /></button>
                    </div>

                    {/* Um aviso só para as duas fontes (camadas internas e Teams) —
                        o usuário não precisa saber qual das duas ainda está vindo. */}
                    {(interno.carregando || agenda.carregandoEventos) && (
                        <span style={{ fontSize: '12px', color: '#999' }}>carregando…</span>
                    )}

                    {estado === 'conectado' && (
                        <button title="Atualizar" onClick={() => carregarEventos()} style={btnNav}>
                            <RefreshCw size={16} style={{ opacity: agenda.carregandoEventos ? 0.4 : 1 }} />
                        </button>
                    )}

                    {/* Novo ▾ — o tipo é escolhido no menu; a cor de cada item é a da
                        camada, a mesma da tag que vai nascer no grid.
                        Some inteiro quando não há nada a criar: um menu que abre só
                        para dizer "aniversário vem do RH" é uma porta para lugar nenhum. */}
                    {tiposNovos.length > 0 && (
                    <div ref={menuRef} style={{ position: 'relative' }}>
                        <button
                            onClick={() => setMenuAberto((v) => !v)}
                            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', backgroundColor: COR.vermelhoEscuro, color: '#fff', border: 'none', borderRadius: '8px', padding: '7px 12px', fontFamily: FONTE, fontWeight: 700, fontSize: '13px', cursor: 'pointer' }}
                        >
                            <Plus size={15} /> Novo <ChevronDown size={14} />
                        </button>

                        {menuAberto && (
                            <div style={{ position: 'absolute', right: 0, top: '38px', width: '210px', backgroundColor: '#fff', borderRadius: '10px', boxShadow: '0 4px 12px rgba(0,0,0,0.18)', padding: '6px 0', zIndex: 40 }}>
                                {tiposNovos.map((t) => (
                                    <button
                                        key={t.chave}
                                        onClick={() => { setMenuAberto(false); t.abrir(); }}
                                        style={{ display: 'flex', alignItems: 'center', gap: '9px', width: '100%', textAlign: 'left', background: 'none', border: 'none', padding: '9px 14px', cursor: 'pointer', fontFamily: FONTE, fontSize: '13px', fontWeight: 600, color: COR.texto }}
                                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f7f6f6'; }}
                                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                                    >
                                        <span style={{ width: '12px', height: '12px', flexShrink: 0, borderRadius: '3px', backgroundColor: t.fundo, border: `1px solid ${t.cor}` }} />
                                        {t.rotulo}
                                    </button>
                                ))}
                                {/* Aniversário nunca é criado aqui — explica em vez de sumir
                                    sem motivo (o usuário vê a camada na legenda e procura). */}
                                <div style={{ borderTop: `1px solid ${COR.bordaSuave}`, margin: '5px 0 0', padding: '8px 14px 4px', fontSize: '11px', color: '#999', lineHeight: 1.4 }}>
                                    Aniversário vem do cadastro de funcionários (RH).
                                </div>
                            </div>
                        )}
                    </div>
                    )}

                    <button onClick={onFechar} title="Fechar" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#999', display: 'flex', padding: '4px' }}>
                        <X size={20} />
                    </button>
                </div>
            </div>

            {/* ---- Estados de conexão do Teams ----
                O calendário NÃO some quando o Teams está fora do ar/desconectado —
                feriados, eventos e aniversários continuam valendo. As faixas abaixo
                explicam só o que falta (a camada roxa). */}
            {estado === 'desconectado' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', backgroundColor: CAMADA.teams.fundo, borderRadius: '8px', padding: '10px 14px', marginBottom: '12px' }}>
                    <span style={{ fontSize: '13px', color: COR.texto }}>
                        Conecte sua conta Microsoft para ver seus compromissos do Teams aqui.
                    </span>
                    <button
                        onClick={agenda.conectar}
                        disabled={agenda.conectando}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', border: 'none', borderRadius: '8px', padding: '7px 12px', backgroundColor: CAMADA.teams.cor, color: '#fff', fontSize: '13px', fontWeight: 700, cursor: agenda.conectando ? 'default' : 'pointer', fontFamily: FONTE }}
                    >
                        <LinkIcon size={15} /> {agenda.conectando ? 'Abrindo…' : 'Conectar agenda do Teams'}
                    </button>
                </div>
            )}
            {estado === 'indisponivel' && (
                <div style={{ fontSize: '12px', color: '#888', marginBottom: '10px' }}>
                    Integração com o Teams ainda não configurada no servidor — o calendário mostra só feriados, eventos e aniversários.
                </div>
            )}
            {estado === 'erro' && (
                <div style={{ fontSize: '12px', color: '#dc2626', marginBottom: '10px' }}>
                    Não foi possível falar com o Teams — as demais camadas seguem abaixo.{' '}
                    <button onClick={agenda.carregarStatus} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#dc2626', textDecoration: 'underline', padding: 0, fontFamily: FONTE, fontSize: '12px' }}>
                        Tentar de novo
                    </button>
                </div>
            )}
            {agenda.erroMsg && !form && (
                <div style={{ fontSize: '12px', color: '#dc2626', marginBottom: '10px' }}>{agenda.erroMsg}</div>
            )}

            {/* ---- Legenda ---- */}
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '12px', fontSize: '12px', color: '#777' }}>
                {Object.entries(CAMADA).map(([chave, c]) => (
                    <span key={chave} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ width: '12px', height: '12px', borderRadius: '3px', backgroundColor: c.fundo, border: `1px solid ${c.cor}` }} />
                        {c.rotulo}
                    </span>
                ))}
            </div>

            {/* ---- Grid ---- */}
            <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
                <GridMes
                    ano={ano}
                    mes={mes}
                    renderDia={renderDia}
                    alturaCelula="92px"
                    // Clicar num dia abre um compromisso do Teams naquela data — é o
                    // tipo "meu" do calendário, o único que se cria em 1 clique. Os
                    // outros passam pelo menu "Novo" (têm campos próprios).
                    // Mesma dupla condição do menu: sem permissão, clicar no dia abriria
                    // um formulário que só levaria 403 ao salvar.
                    onClicarDia={estado === 'conectado' && agenda.podeCriar ? (dia) => setForm(novoCompromisso(new Date(ano, mes - 1, dia))) : undefined}
                />
            </div>

            {/* ---- Rodapé: conta conectada · tela cheia ---- */}
            <div style={{ marginTop: '12px', paddingTop: '10px', borderTop: `1px solid ${COR.bordaSuave}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                <div style={{ fontSize: '11px', color: '#999', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {estado === 'conectado' && agenda.email ? (
                        <>
                            {agenda.email}
                            <button onClick={agenda.desconectar} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: COR.vermelhoEscuro, fontSize: '11px', textDecoration: 'underline', fontFamily: FONTE, marginLeft: '8px' }}>
                                Desconectar
                            </button>
                        </>
                    ) : null}
                </div>
                <button
                    onClick={() => { onFechar(); navigate('/aniversariantes'); }}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', border: 'none', background: 'transparent', cursor: 'pointer', color: COR.vermelhoEscuro, fontSize: '12px', fontWeight: 600, fontFamily: FONTE }}
                >
                    Abrir calendário em tela cheia <ExternalLink size={13} />
                </button>
            </div>

            {/* ---- Formulários por tipo (todos empilham acima deste modal) ---- */}
            {form && (
                <FormCompromisso
                    form={form}
                    setForm={setForm}
                    erroMsg={agenda.erroMsg}
                    onSalvar={salvarForm}
                    onExcluir={excluirForm}
                    onFechar={() => { setForm(null); agenda.setErroMsg(''); }}
                />
            )}
            {eventoAberto && (
                <FormEvento
                    ano={ano}
                    mes={mes}
                    opcoes={interno.opcoes}
                    onFechar={() => setEventoAberto(false)}
                    // `aviso` = criou no portal, mas o espelho no Teams falhou.
                    // Reusa a faixa de erro da agenda em vez de inventar outra.
                    onCriou={(aviso) => { setEventoAberto(false); if (aviso) agenda.setErroMsg(aviso); interno.recarregarEventos(); }}
                />
            )}
            {feriadosAberto && (
                <PainelFeriados
                    ano={ano}
                    mes={mes}
                    feriados={interno.feriados}
                    onFechar={() => setFeriadosAberto(false)}
                    onMudou={interno.recarregarFeriados}
                />
            )}
        </div>
    );
}

export default CalendarioAgenda;
